import { useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Checkbox from 'shared/ui/Checkbox'
import Flex from 'shared/ui/Flex'
import { useClickToCopy } from 'shared/hooks/useClickToCopy'
import {
  RankingQuestionResultFragment,
  RankingsQuestionFragment
} from 'shared/types/gradoo/graphql'

import Option from './components/RankingsOption'

const Header = styled.div<{ $hasCheckbox?: boolean }>`
  margin-bottom: 9px;
  padding-right: ${({ $hasCheckbox }) =>
    $hasCheckbox ? 40 : 0}px;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 11px;
`

const Count = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.color.base.c4};
`

const ShowAllButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  font-weight: 600;
  font-size: 10px;
  color: ${({ theme }) => theme.color.brand_02};
  text-decoration: underline;
  cursor: pointer;
`

const ItemContainer = styled(Flex)<{ $marked?: boolean }>`
  position: relative;
  width: 100%;
  padding: 12px 14px 14px;
  border-radius: 10px;
  border: 2px solid
    ${({ $marked, theme }) =>
      $marked ? theme.color.brand_02 : 'transparent'};
  opacity: ${({ $marked }) => ($marked ? 0.5 : 1)};
  transition:
    border-color 0.15s ease-in-out,
    opacity 0.15s ease-in-out;
`

const CheckboxWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
`

type RankingsItemProps = {
  'data-testid'?: string
  rankingsQuestion: RankingsQuestionFragment
  isMarked?: boolean
  onToggleMark?: () => void
}

const RankingsItem: React.FC<RankingsItemProps> = ({
  rankingsQuestion,
  isMarked,
  onToggleMark,
  ...rest
}) => {
  const { formatMessage } = useIntl()
  const { register } = useClickToCopy()

  const [showAll, setShowAll] = useState(false)

  const rankingQuestionResultOptions =
    (rankingsQuestion.results
      ?.options as RankingQuestionResultFragment[]) || []

  const sortedOptions = rankingQuestionResultOptions
    .filter(op => !!op)
    .sort(
      (op1, op2) => Number(op2.percentage) - Number(op1.percentage)
    )

  const clippedOptions = showAll
    ? sortedOptions
    : sortedOptions.slice(0, 3)
  const hasHiddenOptions = sortedOptions.length > 3

  return (
    <ItemContainer direction="column" width="100%" $marked={isMarked} {...rest}>
      {onToggleMark && (
        <CheckboxWrapper onClick={e => e.stopPropagation()}>
          <Checkbox
            checked={!!isMarked}
            onChange={() => onToggleMark()}
          />
        </CheckboxWrapper>
      )}
      <Header $hasCheckbox={!!onToggleMark}>
        <Title {...register()}>{rankingsQuestion.question}</Title>
      </Header>
      <Flex direction="column" rowGap={10}>
        {clippedOptions.map((rankingQuestionResultOption, i) => (
          <Option
            key={i}
            rankingQuestionResultOption={rankingQuestionResultOption}
          />
        ))}
      </Flex>
      <Flex marginTop={16} justifyContent="space-between">
        <Count>
          {formatMessage(
            { id: 'Panel.rankings.votesCount' },
            {
              voted: rankingsQuestion.results?.alreadyVoted,
              users: rankingsQuestion.results?.possibleVoters
            }
          )}
        </Count>

        {hasHiddenOptions && (
          <ShowAllButton onClick={() => setShowAll(!showAll)}>
            {showAll
              ? formatMessage({ id: 'Panel.rankings.showLess' })
              : formatMessage({ id: 'Panel.rankings.showAll' })}
          </ShowAllButton>
        )}
      </Flex>
    </ItemContainer>
  )
}

export { default as RankingsItemSkeleton } from './Skeleton'
export default RankingsItem
